// Tipos e busca da ajuda interna. A API entrega o manual JÁ CORTADO pelo papel da
// sessão e pelo trilho do projeto (ver apps/api/internal/help): o que chega aqui é o
// que esta pessoa pode ler, e não existe filtro de permissão neste arquivo.

export interface AjudaNo {
  id: string;
  numero: string;
  titulo: string;
  html: string;
  mensagens?: string[];
  menus?: string[];
  figuras?: string[];
  blocos?: string[];
  subsecoes?: AjudaNo[];
}

export interface AjudaCapitulo {
  id: string;
  numero: string;
  titulo: string;
  /** Abertura do capítulo: o que a tela é, antes do primeiro procedimento. */
  html?: string;
  secoes: AjudaNo[];
}

export interface AjudaBusca {
  secao: string; // "capitulo#secao"
  titulo: string;
  tokens: string[];
}

export interface Ajuda {
  fonte_hash: string;
  capitulos: AjudaCapitulo[];
  busca: AjudaBusca[];
  /** Item de menu (nav.numbers) → âncora que o ensina. Alimenta o "?" de cada tela. */
  telas?: Record<string, string>;
}

export function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Procura no índice que veio pronto do gerador.
 *
 * O índice guarda o título e as mensagens do sistema repetidos, então quem digita o
 * texto de um erro ("número conectado") cai na seção que o explica, e não numa que
 * só o menciona de passagem. Cada palavra digitada precisa aparecer em algum token —
 * procurar "conectar numero" não pode trazer tudo que fala de número.
 */
export function procurar(indice: AjudaBusca[], termo: string): Set<string> {
  const palavras = semAcento(termo).match(/[a-z0-9]{2,}/g) ?? [];
  if (!palavras.length) return new Set();

  const pontos = new Map<string, number>();
  for (const entrada of indice) {
    let total = 0;
    for (const palavra of palavras) {
      const casou = entrada.tokens.filter((t) => t.startsWith(palavra)).length;
      if (!casou) { total = 0; break; } // toda palavra tem de aparecer
      total += casou;
    }
    if (total > 0) pontos.set(entrada.secao, total);
  }
  return new Set([...pontos.keys()]);
}

/** Âncora estável usada na URL: #capitulo ou #capitulo/secao. */
export function ancora(capituloId: string, secaoId?: string): string {
  return secaoId ? `${capituloId}/${secaoId}` : capituloId;
}

export function daAncora(hash: string): { capitulo: string; secao?: string } | null {
  const limpo = hash.replace(/^#/, '').trim();
  if (!limpo) return null;
  const [capitulo, secao] = limpo.split('/');
  return capitulo ? { capitulo, secao: secao || undefined } : null;
}

/**
 * Traduz o que o servidor manda em `telas` ("numeros" ou "numeros#secao") para a
 * âncora da tela da ajuda ("numeros" ou "numeros/secao").
 *
 * São duas grafias porque são dois mundos: `#` é como o manual nomeia uma seção dentro
 * do capítulo, e `/` é o que cabe numa URL sem virar outra âncora.
 */
export function ancoraDaTela(destino: string | undefined): string | null {
  if (!destino) return null;
  const [capitulo, secao] = destino.split('#');
  return capitulo ? ancora(capitulo, secao) : null;
}
